import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth, useUser } from '@clerk/react'
import { useUserStore } from '../../stores/userStore'
import { CONTENT_CONTAINER } from '../../lib/layout'
import { getEffectiveTier } from '../../lib/tier'
import { useFeatureFlag } from '../../hooks/useFeatureFlags'
import { NotificationBell } from './NotificationBell'

const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

export function TopBar() {
  const location = useLocation()
  const { isSignedIn } = useAuth()
  const helpCenterOn = useFeatureFlag('help_center')

  // Hide only on full-screen flows where chrome would interrupt the experience.
  // Legacy marketing surfaces (/terms, pillar pages) keep the TopBar so anon
  // users have a reachable Sign in affordance without scrolling.
  //
  // The handoff-v2 sister pages (/about /pricing /how-it-works) render their own
  // MarketingNav via MarketingLayout — the in-app TopBar would stack a second
  // nav on top, so it hides there.
  //
  // /shared/:shortid is the public chrome-less shared-reading page — it owns
  // its own sign-up CTA and must render with NO app chrome for anonymous
  // recipients.
  const hideOnPaths = [
    '/reading', '/onboarding', '/sign-in', '/sign-up',
    '/about', '/pricing', '/how-it-works', '/shared',
  ]
  if (hideOnPaths.some(p => location.pathname.startsWith(p))) return null

  // /help renders the marketing HelpPage (its own MarketingNav) unless a
  // signed-in viewer has the help_center flag on — then it's the in-app Help
  // center, which wants the app TopBar. Flag off / signed out ⇒ hide (marketing).
  if (location.pathname.startsWith('/help') && !(isSignedIn && helpCenterOn)) return null

  // The admin portal (/admin/*) owns its own full-screen chrome (left rail +
  // topbar), so the in-app TopBar hides on these routes.
  if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) return null

  // The unauthenticated landing v2 (/) also owns its chrome via MarketingLayout.
  // Signed-in users at / render <HomePage> instead, so we only hide the in-app
  // TopBar at / when the visitor is not signed in. `isSignedIn === undefined`
  // means Clerk hasn't loaded yet (or is disabled) — treat that as anon so
  // the prerender snapshot + first-paint flash match.
  if (location.pathname === '/' && !isSignedIn) return null

  return (
    <header
      data-app-topbar
      className="shrink-0 z-40 bg-cosmic-topbar border-b border-hairline px-4 md:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-between"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Logo />
        <BetaPill />
      </div>
      <div className="flex items-center gap-2.5 shrink-0 pl-2">
        {isSignedIn ? <NotificationBell /> : null}
        {CLERK_ENABLED ? <AuthenticatedRight /> : <UnauthenticatedRight />}
      </div>
    </header>
  )
}

function Logo() {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => navigate('/')}
      className="font-display text-base sm:text-lg md:text-xl font-light tracking-cosmic text-text-primary truncate min-w-0"
    >
      The <span className="text-gold">Cosmic</span> Finder App
    </button>
  )
}

function BetaPill() {
  const profile = useUserStore((s) => s.profile)
  if (profile?.role !== 'beta_tester') return null
  return (
    <span
      title="You're in the beta — thank you for walking this in with us."
      className="shrink-0 text-[9px] tracking-[0.2em] uppercase font-body font-medium px-1.5 py-0.5 rounded-full bg-gold/10 text-gold/70 border border-gold/20"
    >
      beta
    </span>
  )
}

function AuthenticatedRight() {
  const { isSignedIn, isLoaded } = useAuth()
  const navigate = useNavigate()

  if (!isLoaded) {
    return <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-hairline-faint animate-pulse" />
  }

  if (!isSignedIn) {
    return (
      <button
        onClick={() => navigate('/sign-in')}
        className="text-xs md:text-sm font-body text-gold/90 hover:text-gold transition-colors tracking-cosmic whitespace-nowrap"
      >
        Sign in
      </button>
    )
  }

  return (
    <>
      <TierBadge />
      <AvatarButton />
    </>
  )
}

function UnauthenticatedRight() {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => navigate('/sign-in')}
      className="text-xs font-body text-gold/90 hover:text-gold transition-colors tracking-cosmic whitespace-nowrap"
    >
      Sign in
    </button>
  )
}

function TierBadge() {
  const profile = useUserStore((s) => s.profile)
  if (!profile || getEffectiveTier(profile) !== 'paid') return null
  return (
    <span className="hidden sm:inline text-xs text-gold/90 tracking-cosmic font-body">
      Cosmic
    </span>
  )
}

function AvatarButton() {
  const { user } = useUser()
  const navigate = useNavigate()

  const initials = user
    ? (user.firstName?.[0] || '') + (user.lastName?.[0] || user.firstName?.[1] || '')
    : '?'

  return (
    <button
      onClick={() => navigate('/profile')}
      className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-deep-purple border border-gold/30
                 flex items-center justify-center text-xs text-gold font-medium
                 hover:border-gold/50 transition-colors"
    >
      {user?.imageUrl ? (
        <img src={user.imageUrl} alt="" className="w-full h-full rounded-full object-cover" />
      ) : (
        initials.toUpperCase()
      )}
    </button>
  )
}
